/**
 * Chronicle — GDD §16.
 *
 * The Chronicle is not a log dump. It is the game's argument that your choices
 * mattered, so it is generated from `pivotal`+ events only, and every line cites
 * the roll that caused it when one exists. "The 6 in the flooded hall" is a far
 * stronger memory than "you defeated a goblin".
 */

import type { EventLog, GameEvent } from './events.js';
import type { GameState } from './state.js';

export interface ChronicleChapter {
  readonly chapter: number;
  readonly title: string;
  readonly lines: readonly string[];
  readonly fromSeq: number;
  readonly toSeq: number;
}

interface Phrasing {
  /** Title fragment when this event opens a chapter. */
  readonly chapterTitle?: (e: GameEvent) => string;
  readonly line: (e: GameEvent, names: NameLookup) => string;
}

export type NameLookup = (id: string) => string;

const identity: NameLookup = (id) => id;

const d = (e: GameEvent, key: string): string => {
  const v = e.data?.[key];
  return typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
};

/**
 * Event-to-prose table. Kept in code rather than content data because these are
 * *structural* sentences — one per event type, reused across all content — and
 * the localisation unit is this table, not each NPC.
 */
const PHRASINGS: Partial<Record<GameEvent['type'], Phrasing>> = {
  RUN_STARTED: {
    chapterTitle: () => '첫 번째 굴림',
    line: () => '손 안의 주사위가 아직 차갑다. 아무것도 결정되지 않았다.',
  },
  NPC_SAVED: {
    chapterTitle: (e) => `${e.target ? '구원' : '구원'}`,
    line: (e, n) => `${n(e.target ?? '누군가')}를 살렸다. 그는 그것을 잊지 않을 것이다.`,
  },
  NPC_KILLED: {
    chapterTitle: () => '피',
    line: (e, n) => `${n(e.target ?? '누군가')}를 죽였다. 남은 이들이 그 이름을 기억한다.`,
  },
  NPC_BETRAYED: {
    chapterTitle: () => '배신',
    line: (e, n) => `${n(e.target ?? '누군가')}의 신뢰를 이용했다. 대가는 아직 청구되지 않았다.`,
  },
  PROMISE_MADE: {
    line: (e, n) => `${n(e.target ?? '누군가')}에게 약속했다. "${d(e, 'description')}"`,
  },
  PROMISE_KEPT: {
    line: (e, n) => `${n(e.target ?? '누군가')}와의 약속을 지켰다.`,
  },
  PROMISE_BROKEN: {
    chapterTitle: () => '깨진 약속',
    line: (e, n) => `${n(e.target ?? '누군가')}와의 약속을 지키지 못했다. 그는 기다리고 있었다.`,
  },
  BOSS_KILLED: {
    chapterTitle: (e) => `${e.target ? '왕관의 끝' : '끝'}`,
    line: (e, n) => `${n(e.target ?? '그것')}을 쓰러뜨렸다.`,
  },
  BOSS_SPARED: {
    chapterTitle: () => '내려놓은 칼',
    line: (e, n) => `${n(e.target ?? '그것')}을 살려 보냈다. 세계가 그 선택을 기록했다.`,
  },
  BOSS_RECRUITED: {
    chapterTitle: () => '뜻밖의 동행',
    line: (e, n) => `${n(e.target ?? '그것')}이 곁에 섰다. 아무도 예상하지 못한 일이다.`,
  },
  MONSTER_SPARED: {
    line: (e, n) => `${n(e.target ?? '그것')}을 놓아주었다.`,
  },
  SECRET_DISCOVERED: {
    chapterTitle: () => '벽 뒤의 것',
    line: (e) => `숨겨진 것을 찾아냈다. (${d(e, 'secretId')})`,
  },
  EASTER_EGG_FOUND: {
    chapterTitle: () => '있을 수 없는 일',
    line: (e) => `설명할 수 없는 것을 목격했다. (${d(e, 'eggId')})`,
  },
  FATE_DEBT_THRESHOLD: {
    chapterTitle: () => '운명의 청구서',
    line: (e) => `운명을 너무 여러 번 고쳐 썼다. 빚이 ${d(e, 'threshold')}에 닿았다.`,
  },
  PLAYER_DIED: {
    chapterTitle: () => '죽음',
    line: () => '여기서 한 번 끝났다. 그러나 이야기는 끝나지 않았다.',
  },
  TITLE_EARNED: {
    line: (e) => `'${d(e, 'title')}'로 불리기 시작했다.`,
  },
  CARD_TRANSFORMED: {
    line: (e) => `${d(e, 'from')}이(가) ${d(e, 'to')}으로 변했다. 되돌릴 수 없다.`,
  },
  RUN_ENDED: {
    chapterTitle: (e) =>
      d(e, 'reason') === 'victory'
        ? '마지막 굴림'
        : d(e, 'reason') === 'death'
          ? '남겨진 것'
          : '접힌 페이지',
    // A death already has its own chapter (PLAYER_DIED), so this line must not
    // repeat it or contradict it by calling a death an abandonment.
    line: (e) => {
      switch (d(e, 'reason')) {
        case 'victory':
          return '모험이 끝났다. 세계는 조금 달라졌다.';
        case 'death':
          return '이야기는 여기서 멈췄다. 그러나 네가 한 일은 그대로 남았다.';
        default:
          return '모험이 중단되었다. 세계는 그것도 기억한다.';
      }
    },
  },
};

/**
 * Build chapters from the log. A new chapter opens on any event whose phrasing
 * supplies a `chapterTitle`, so chapters follow the story's turning points
 * rather than a fixed event count.
 */
export function buildChronicle(log: EventLog, names: NameLookup = identity): ChronicleChapter[] {
  const significant = log.atLeast('pivotal');
  const chapters: ChronicleChapter[] = [];
  let current: {
    title: string;
    lines: string[];
    fromSeq: number;
    toSeq: number;
  } | null = null;

  for (const e of significant) {
    const phrasing = PHRASINGS[e.type];
    if (!phrasing) continue;
    const line = withRollCitation(phrasing.line(e, names), e);

    if (phrasing.chapterTitle) {
      if (current) chapters.push({ chapter: chapters.length + 1, ...current });
      current = {
        title: phrasing.chapterTitle(e),
        lines: [line],
        fromSeq: e.seq,
        toSeq: e.seq,
      };
    } else if (current) {
      current.lines.push(line);
      current.toSeq = e.seq;
    } else {
      current = { title: '시작', lines: [line], fromSeq: e.seq, toSeq: e.seq };
    }
  }

  if (current) chapters.push({ chapter: chapters.length + 1, ...current });
  return chapters;
}

/** Attach "(N번째 굴림)" when the event knows which roll caused it. */
function withRollCitation(line: string, e: GameEvent): string {
  return e.rollRef !== undefined ? `${line} — ${e.rollRef}번째 굴림` : line;
}

export interface RunSummary {
  readonly rolls: number;
  readonly manipulations: number;
  readonly deaths: number;
  readonly npcsMet: number;
  readonly npcsSaved: number;
  readonly npcsKilled: number;
  readonly monstersKilled: number;
  readonly monstersSpared: number;
  readonly secrets: number;
  readonly easterEggs: number;
  readonly fateDebt: number;
  readonly faceDistribution: readonly number[];
  /** The single most consequential roll of the run, by events it caused. */
  readonly definingRoll: { seq: number; causedEvents: number } | null;
  readonly titles: readonly string[];
  readonly dominantTrait: string | null;
}

/** GDD §16 Annual Summary, at run scale. Shareable numbers. */
export function summarizeRun(state: GameState, log: EventLog): RunSummary {
  const agg = state.world.aggregates;
  const faceDistribution = [1, 2, 3, 4, 5, 6].map((f) => agg[`rolls.face.${f}`] ?? 0);

  let definingRoll: { seq: number; causedEvents: number } | null = null;
  const byRoll = new Map<number, number>();
  for (const e of log.atLeast('notable')) {
    if (e.rollRef === undefined) continue;
    byRoll.set(e.rollRef, (byRoll.get(e.rollRef) ?? 0) + 1);
  }
  for (const [seq, count] of byRoll) {
    if (!definingRoll || count > definingRoll.causedEvents) {
      definingRoll = { seq, causedEvents: count };
    }
  }

  let dominant: string | null = null;
  let bestVal = 0;
  for (const [k, v] of Object.entries(state.player.traits)) {
    if ((v ?? 0) > bestVal) {
      bestVal = v ?? 0;
      dominant = k;
    }
  }

  return {
    rolls: agg['rolls'] ?? 0,
    manipulations: agg['manipulations'] ?? 0,
    deaths: agg['deaths'] ?? 0,
    npcsMet: agg['npcsMet'] ?? 0,
    npcsSaved: agg['npcsSaved'] ?? 0,
    npcsKilled: agg['npcsKilled'] ?? 0,
    monstersKilled: agg['monstersKilled'] ?? 0,
    monstersSpared: agg['monstersSpared'] ?? 0,
    secrets: agg['secrets'] ?? 0,
    easterEggs: agg['easterEggs'] ?? 0,
    fateDebt: state.dice.fateDebt,
    faceDistribution,
    definingRoll,
    titles: state.player.titles,
    dominantTrait: dominant,
  };
}

/** Titles are earned from behaviour, and some of them open dialogue (GDD §16). */
export interface TitleRule {
  readonly title: string;
  readonly test: (s: GameState, log: EventLog) => boolean;
}

export const TITLE_RULES: readonly TitleRule[] = [
  {
    title: 'Dragon Slayer',
    test: (s) => (s.world.aggregates['bossesKilled'] ?? 0) >= 1,
  },
  {
    title: 'Merciful',
    test: (s) =>
      (s.world.aggregates['monstersSpared'] ?? 0) + (s.world.aggregates['bossesSpared'] ?? 0) >= 3,
  },
  {
    title: 'Oathkeeper',
    test: (s) => (s.player.counters['promisesKept'] ?? 0) >= 3 && (s.player.counters['promisesBroken'] ?? 0) === 0,
  },
  {
    title: 'Oathbreaker',
    test: (s) => (s.player.counters['promisesBroken'] ?? 0) >= 2,
  },
  {
    title: 'Fate Debtor',
    test: (s) => s.dice.fateDebt >= 60,
  },
  {
    title: 'Wall-Listener',
    test: (s) => s.world.discoveredSecrets.size >= 2,
  },
  {
    title: 'Unlucky',
    test: (s) => (s.world.aggregates['badLuckStreaks'] ?? 0) >= 2,
  },
  {
    title: 'Hand of Fortune',
    test: (s) => (s.world.aggregates['fateChains'] ?? 0) >= 2,
  },
];

export function checkTitles(state: GameState, log: EventLog): string[] {
  return TITLE_RULES.filter((r) => !state.player.titles.includes(r.title) && r.test(state, log)).map(
    (r) => r.title,
  );
}
